import clone from 'clone'
import { computed, onMounted } from 'vue'

import { useImgSrcSet } from '../composables/use-img-src-set'
import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import { useRsVersionStore } from '../stores/rsVersion'
import { useSessionStore } from '../stores/session'
import { useSourcepointStore } from '../stores/sourcepoint'

import type { Ref } from 'vue'
import type { ComposedPage } from '@xcel-next/composer-schema'

type HeadScript = {
  'data-vmid'?: string,
  key?: string,
  id?: string,
  type?: string,
  src?: string,
  tagPriority?: number,
  innerHTML?: string,
  async?: boolean
}
type HeadLink = {
  'data-vmid'?: string,
  key?: string,
  rel?: string,
  href?: string,
  as?: string,
  tagPriority?: number,
  appendVersion?: boolean
}
type HeadMeta = {
  name?: string,
  property?: string,
  'data-vmid'?: string,
  content?: string | null
}
type HeadConfig = {
  htmlAttrs: { lang: string },
  title: string,
  meta: HeadMeta[],
  link: HeadLink[],
  script: HeadScript[]
}

export function useMeta (pageData: Ref<ComposedPage>, brand: Ref<string>) {
  const { createImgSrc } = useImgSrcSet()
  const configStore = useConfigStore()
  const pageStore = usePageStore()
  const rsVersionStore = useRsVersionStore()
  const sessionStore = useSessionStore()
  const sourcepointStore = useSourcepointStore()

  const title = computed(() => {
    const pageSuffix = (pageData.value?.pageMeta?.page ?? 0) > 1 ? ` - Seite ${pageData.value.pageMeta.page}` : ''
    const metaTitle = pageData.value?.metaData?.metaTitle
    const headline = pageData.value?.body?.headline
    const brand = configStore.rsConfig.actualBrandName
    const pipedBrandSuffix = configStore.rsConfig.appendBrandToMetaTitle ? ` | ${brand}` : ''
    return metaTitle
      ? `${metaTitle}${pageSuffix}${pipedBrandSuffix}`
      : (headline ? `${headline}${pageSuffix}${pipedBrandSuffix}` : (brand + pageSuffix))
  })

  function isEpgPage (pageType: string | undefined, url: string | undefined): boolean {
    return Boolean(
      // - epg-channel: "/tv/sender-xxx" (e.g. "/tv/sender-rtl")
      url?.startsWith('/tv/sender-') ||
      // - epg-broadcast: "/tv/xxx-epg-12345" (e.g. "/tv/hubert-ohne-staller-epg-208419827")
      (/^\/tv\/.*-epg-\d+$/.test(url ?? '')) ||
      // - structure-terms (tv related): "/tv/..." (e.g. "/tv/fernsehprogramm-heute-abend", "/tv/tipps", "/tv/fernsehprogramm-morgen")
      (url?.startsWith('/tv/') && pageType === 'structureterm')
    )
  }

  function isVodPage (url: string | undefined): boolean {
    return /^\/wer-streamt$/.test(url ?? '') || /^\/wer-streamt\/.*-vod-\d+$/.test(url ?? '')
  }

  function handleSearchParamsPoly (urlNode: URL, action: 'set' | 'delete', key: string, value: number | null): void {
    const params: Record<string, string> = {}
    urlNode.search.replace(/[?&]+([^=&]+)=([^&]*)/gi, (_s, k, v) => { params[k] = v; return '' })

    if (action === 'set' && key !== null) {
      params[key] = String(value)
    } else if (action === 'delete' && key !== null && params[key] !== undefined) {
      delete params[key]
    }

    urlNode.search = Object.keys(params).map((k) => {
      return encodeURIComponent(k) + '=' + encodeURIComponent(params[k])
    }).join('&')
  }

  function extractMetas (): HeadMeta[] {
    const metas: HeadMeta[] = []

    if (pageData.value?.metaData) {
      const {
        metaTitle,
        metaDescription,
        ogTitle,
        ogDescription,
        canonical,
        ogImage,
        noIndex,
        noIndexFollow,
        noIndexNoFollow
      } = pageData.value.metaData

      if (metaTitle) {
        metas.push({ name: 'title', 'data-vmid': 'title', content: title.value })
      }
      if (metaDescription) {
        metas.push({ name: 'description', 'data-vmid': 'description', content: metaDescription })
      }

      // https://bauerxcel.atlassian.net/browse/NXT-9183
      const ogSiteName = configStore.rsConfig.actualBrandName
      if (ogSiteName) {
        metas.push({ property: 'og:site_name', 'data-vmid': 'og-site-name', content: ogSiteName })
      }
      if (ogTitle) {
        metas.push({ property: 'og:title', 'data-vmid': 'og-title', content: ogTitle })
      }
      if (ogDescription) {
        metas.push({ property: 'og:description', 'data-vmid': 'og-description', content: ogDescription })
      }
      if (ogImage) {
        const ogImageObject = createImgSrc({
          image: ogImage,
          brand: brand.value,
          setName: 'ogImage'
        })
        metas.push({ property: 'og:image', 'data-vmid': 'og-image', content: ogImageObject })
      }

      // https://bauerxcel.atlassian.net/browse/NXT-8597
      if (canonical) {
        metas.push({ property: 'og:url', content: canonical })
      }

      const robotsContent: string[] = []
      if (noIndex) {
        robotsContent.push('noindex')
        if (noIndexNoFollow) robotsContent.push('nofollow')
        else if (noIndexFollow) robotsContent.push('follow')
      } else {
        robotsContent.push('index', 'follow')
      }

      if (pageStore.isArticleOrArticleVersion) {
        robotsContent.push('max-image-preview:large')
      }

      metas.push({ name: 'robots', 'data-vmid': 'robots', content: robotsContent.join(',') })
    }

    if (pageData.value?.pageMeta) {
      const { pageType, structureTermType, url, composerVersion } = pageData.value.pageMeta || {}
      const isHome = url === '/'
      const isEpg = isEpgPage(pageType, url)
      const isVod = isVodPage(url)

      // https://bauerxcel.atlassian.net/browse/NXT-8596
      // we don't want no epg and vod pages in the google news as they're not relevant and prevent actual news articles from being indexed.
      // so we set `<meta name="googlebot-news" content="noindex">` for these pages.
      if (isEpg || isVod) {
        metas.push({ name: 'googlebot-news', 'data-vmid': 'googlebot-news', content: 'noindex' })
      }

      // https://bauerxcel.atlassian.net/browse/NXT-8597?focusedCommentId=63926
      metas.push({
        property: 'og:type',
        content: !isHome && !isEpg && !isVod && (pageStore.isArticleOrArticleVersion || (pageType === 'structureterm' && structureTermType !== 'static'))
          ? 'article'
          : 'website'
      })

      if (composerVersion) {
        metas.push({ name: 'generator', 'data-vmid': 'generator-cs', content: 'composer v' + composerVersion })
      }
      metas.push({ name: 'generator', 'data-vmid': 'generator-rs', content: 'renderer v' + process.env.RS_VERSION })
    }

    return metas
  }

  function evaluateMetas (): HeadMeta[] {
    return [...(clone(configStore.rsConfig.htmlHeadMetas) ?? []), ...extractMetas()]
  }

  function reloadConsentScript (): void {
    // Remove existing consent script
    document.querySelector('[data-vmid="wrapper-without-detection"]')?.remove()
    // re-add script
    const script = document.createElement('script')
    script.src = `${sourcepointStore.consentConfig.wrapperWithoutDetection}`
    script.async = true
    script.dataset.vmid = 'wrapper-without-detection'
    document.head.appendChild(script)
  }

  function generateMetas (): HeadConfig {
    let setHtmlHeadScriptSources: HeadScript[] = []
    if (!sourcepointStore.consentConfig.isConsentWhitelistPage) {
      setHtmlHeadScriptSources = clone(configStore.rsConfig.htmlHeadScriptSources) || []
    }

    let headLinks: HeadLink[] = clone(configStore.rsConfig.htmlHeadLinks) ?? []
    // append version to css files and their preloads
    headLinks = headLinks.map((element) => {
      if (element.appendVersion) {
        const { appendVersion: _, ...rest } = element
        return { ...rest, href: `${element.href}?v=${rsVersionStore.rsVersion}` }
      }
      return element
    })

    const head: HeadConfig = {
      htmlAttrs: { lang: 'de' },
      title: title.value,
      meta: evaluateMetas(),
      link: headLinks,
      script: setHtmlHeadScriptSources
    }

    if (pageData.value?.metaData) {
      const overrides = configStore.rsConfig?.structuredDataOverrides
      const currentUrl = pageData.value.pageMeta?.url
      let dataToInject: Record<string, unknown>[] = []

      // check if structuredDataOverrides exist
      if (overrides && overrides.length > 0 && currentUrl) {
        const matchedOverride = overrides.find((override) => {
          return override.urlPattern.test(currentUrl)
        })
        if (matchedOverride) {
          // take data from the override
          dataToInject = [matchedOverride.data]

          // append generic SiteNavigationElements
          if (pageData.value.metaData.structuredData) {
            const genericNav = pageData.value.metaData.structuredData.filter(
              (elem) => elem['@type'] === 'SiteNavigationElement'
            )
            dataToInject.push(...genericNav)
          }
        }
      }

      // if there are no overrides, use all generic data
      if (dataToInject.length === 0 && pageData.value.metaData.structuredData) {
        dataToInject = pageData.value.metaData.structuredData
      }

      // inject data into head
      head.script.push(...dataToInject.map((elem) => ({
        innerHTML: JSON.stringify(elem),
        'data-vmid': 'structured-data',
        type: 'application/ld+json'
      })))
    }

    const { baseUrl, page, numPages, teaserFilter } = pageData.value?.pageMeta || {}
    const { canonical } = pageData.value?.metaData || {}

    if (canonical) {
      head.link.push({ rel: 'canonical', 'data-vmid': 'canonical', href: canonical })

      if (page && numPages) {
        if (page > 1) {
          const prev = new URL(canonical, baseUrl)
          if (page === 2) {
            handleSearchParamsPoly(prev, 'delete', 'page', null)
          } else {
            if (teaserFilter) prev.searchParams.set('filtered', teaserFilter)
            handleSearchParamsPoly(prev, 'set', 'page', page - 1)
          }
          head.link.push({ 'data-vmid': 'prev', rel: 'prev', href: prev.href })
        }
        if (numPages > page) {
          const next = new URL(canonical, baseUrl)
          if (teaserFilter) next.searchParams.set('filtered', teaserFilter)
          handleSearchParamsPoly(next, 'set', 'page', page + 1)
          head.link.push({ 'data-vmid': 'next', rel: 'next', href: next.href })
        }
      }
    }

    // *********** IMPORTANT *********** These scripts must be the first scripts to load and in this order!!!!
    // Sourcepoint needs to be loaded for additional consent (embeds) and to open the consent manager on the datenschutz page
    // even if consent is not really required with PUR abo. For these cases we just hide the popup with the `hidePrivacyManager` setting below
    // consentConfig is from sourcepointStore and imported by component that uses this mixin
    // IMPORTANT: The Sourcepoint stub and configuration must execute in this exact order.
    // tagPriority preserves their position while the messaging wrapper loads asynchronously.

    // Only needed to display the data protection manager manually
    head.script.unshift({
      key: 'open-privacy-manager',
      'data-vmid': 'open-privacy-manager',
      type: 'text/javascript',
      async: false,
      tagPriority: 6,
      innerHTML: `window.openPrivacyManager = function() {
          window._sp_.loadPrivacyManagerModal(${sourcepointStore.consentConfig.privacyManagerId})
        }`
    })

    head.script.unshift({
      key: 'wrapper-without-detection',
      'data-vmid': 'wrapper-without-detection',
      type: 'text/javascript',
      async: true,
      tagPriority: 5,
      src: sourcepointStore.consentConfig.wrapperWithoutDetection
    })

    head.script.unshift({
      key: 'sp-config',
      'data-vmid': 'sp-config',
      type: 'text/javascript',
      async: false,
      tagPriority: 4,
      innerHTML: `
        window._sp_ = {
          config: {
            accountId: ${sourcepointStore.consentConfig.accountId},
            propertyId: ${sourcepointStore.consentConfig.propertyId},
            baseEndpoint: '${sourcepointStore.consentConfig.privacyBaseEndpoint}',
            targetingParams: {
              hidePrivacyManager: ${sessionStore.hasPurSub || sourcepointStore.consentConfig.isConsentWhitelistPage}
            },
            events: {
              onPrivacyManagerActionStatus: function (status) {
                if (status === 'none') {
                  window.location.reload()
                }
              }
            }
          }
        }`
    })

    head.script.unshift({
      key: 'gdpr-stub',
      'data-vmid': 'gdpr-stub',
      type: 'text/javascript',
      async: false,
      tagPriority: 3,
      src: '/js/gdpr-stub.js'
    })

    // Preload GDPR stuff from/for Sourcepoint
    head.link.unshift({
      key: 'wrapper-without-detection-preload',
      'data-vmid': 'wrapper-without-detection-preload',
      rel: 'preload',
      as: 'script',
      tagPriority: 2,
      href: sourcepointStore.consentConfig.wrapperWithoutDetection
    })

    head.link.unshift({
      key: 'gdpr-stub-preload',
      'data-vmid': 'gdpr-stub-preload',
      rel: 'preload',
      as: 'script',
      tagPriority: 1,
      href: '/js/gdpr-stub.js'
    })

    return head
  }

  onMounted(() => {
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        reloadConsentScript()
      }
    })
  })

  return { title, generateMetas, reloadConsentScript, extractMetas, evaluateMetas, isEpgPage, isVodPage, handleSearchParamsPoly }
}
